import { bindable, BindingMode, customElement } from 'aurelia';
import template from './localized-date-input.html';

type NativeDateInput = HTMLInputElement & {
  showPicker?: () => void;
};

@customElement({ name: 'localized-date-input', template })
export class LocalizedDateInput {
  @bindable({ mode: BindingMode.twoWay }) value = '';
  @bindable locale = 'en-GB';
  @bindable disabled = false;
  @bindable label = '';

  public displayValue = '';
  public nativeInput!: NativeDateInput;

  public binding(): void {
    this.syncDisplayValue();
  }

  public valueChanged(): void {
    this.syncDisplayValue();
  }

  public localeChanged(): void {
    this.syncDisplayValue();
  }

  public get placeholder(): string {
    if (this.isGerman) return 'TT.MM.JJJJ';
    return this.isMonthFirst ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
  }

  public get nativeLanguage(): 'de' | 'en-GB' | 'en-US' {
    if (this.isGerman) return 'de';
    return this.isMonthFirst ? 'en-US' : 'en-GB';
  }

  public onTextInput(event: Event): void {
    this.displayValue = (event.target as HTMLInputElement).value;
  }

  public onTextKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.commitTextValue();
  }

  public onTextBlur(): void {
    this.commitTextValue();
  }

  public onNativeInput(event: Event): void {
    this.value = (event.target as HTMLInputElement).value;
  }

  public openPicker(event: MouseEvent): void {
    event.preventDefault();
    this.commitTextValue();
    this.nativeInput.value = this.value;
    if (typeof this.nativeInput.showPicker === 'function') {
      this.nativeInput.showPicker();
    } else {
      this.nativeInput.click();
    }
  }

  private get isGerman(): boolean {
    return this.locale.toLowerCase().startsWith('de');
  }

  private get isMonthFirst(): boolean {
    return this.locale.toLowerCase().startsWith('en-us');
  }

  private syncDisplayValue(): void {
    this.displayValue = this.formatValue(this.value);
  }

  private commitTextValue(): void {
    const text = this.displayValue.trim();
    if (!text) {
      this.value = '';
      return;
    }

    const parsed = this.parseValue(text);
    if (!parsed) {
      this.syncDisplayValue();
      return;
    }

    this.value = parsed;
    this.displayValue = this.formatValue(parsed);
  }

  private formatValue(value: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return value;
    const [, year, month, day] = match;
    if (this.isGerman) return `${day}.${month}.${year}`;
    return this.isMonthFirst
      ? `${month}/${day}/${year}`
      : `${day}/${month}/${year}`;
  }

  private parseValue(value: string): string | null {
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (isoMatch) {
      return this.validIsoDate(isoMatch[1], isoMatch[2], isoMatch[3]);
    }

    const separator = this.isGerman ? '\\.' : '/';
    const localizedMatch = new RegExp(`^(\\d{1,2})${separator}(\\d{1,2})${separator}(\\d{4})$`).exec(value);
    if (!localizedMatch) return null;

    return this.validIsoDate(
      localizedMatch[3],
      (this.isMonthFirst ? localizedMatch[1] : localizedMatch[2]).padStart(2, '0'),
      (this.isMonthFirst ? localizedMatch[2] : localizedMatch[1]).padStart(2, '0'),
    );
  }

  private validIsoDate(year: string, month: string, day: string): string | null {
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    if (
      date.getUTCFullYear() !== Number(year)
      || date.getUTCMonth() !== Number(month) - 1
      || date.getUTCDate() !== Number(day)
    ) {
      return null;
    }

    return `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
}
